import { useState, useEffect, useRef, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Sparkles, Send, Loader2, FileText, X, Check, Edit3, History, BookOpen, Scale, Shield, Database, Clock, FolderOpen, Files, Search, Plus, ExternalLink, ChevronDown, ChevronUp, Gavel, ClipboardList, Trash2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { api, FileUploadResult, SourceItem, OpinionMetadata, OpinionVersion, opinionApi, documentsApi, opinionSessionsApi, OpinionSessionTurnDetail, OpinionSessionListItem, Document } from '../services/api'
import FileUpload from '../components/shared/FileUpload'
import SessionTimeline from '../components/opinion/SessionTimeline'
import { useProject } from '../contexts/ProjectContext'
import { useGeneration } from '../contexts/GenerationContext'
import { useShortcutParamsWithClear } from '../hooks/useShortcutNavigation'

interface GenerateOpinionRequest {
  query: string
  instructions?: string
  documentContent?: string
  useAnonymization?: boolean
  role?: string
  docType?: string
  useUodo?: boolean
  useUzp?: boolean
  useSn?: boolean
  useMs?: boolean
  useEurlex?: boolean
  useCaseLaw?: boolean
  projectId?: string
}

interface OpinionState {
  text: string
  metadata?: OpinionMetadata
  sessionId?: string
  version: number
  status: 'idle' | 'generating' | 'awaiting_feedback' | 'completed' | 'error'
}

// Role options matching backend /api/generation/options
const ROLE_OPTIONS = [
  { value: 'neutral', label: 'Neutralny analityk' },
  { value: 'defender_civil', label: 'Pełnomocnik pozwanego (cywilny)' },
  { value: 'plaintiff_rep', label: 'Pełnomocnik powoda' },
  { value: 'prosecutor', label: 'Oskarżyciel' },
  { value: 'defender_criminal', label: 'Obrońca (karny)' },
  { value: 'business_advisor', label: 'Doradca biznesowy' },
]

// Document type options matching backend
const DOC_TYPE_OPTIONS = [
  { value: 'legal_opinion', label: 'Opinia' },
  { value: 'response_to_claim', label: 'Odpowiedź na pozew' },
  { value: 'claim', label: 'Pozew' },
  { value: 'appeal', label: 'Apelacja' },
  { value: 'cassation', label: 'Skarga kasacyjna' },
  { value: 'memo', label: 'Notatka prawna' },
]

// Helper function to count sources by type
function countSourcesByType(sources: SourceItem[]): { eli: number; eurlex: number; caseLaw: number; uodo: number; uzp: number; sn: number } {
  return sources.reduce(
    (acc, source) => {
      // Handle both camelCase and PascalCase (safety fallback)
      const sourceType = (source as any).type || (source as any).Type || ''
      switch (sourceType) {
        case 'eli':
          acc.eli++
          break
        case 'eurlex':
          acc.eurlex++
          break
        case 'case_law':
          acc.caseLaw++
          break
        case 'uodo':
          acc.uodo++
          break
        case 'uzp':
          acc.uzp++
          break
        case 'sn':
          acc.sn++
          break
      }
      return acc
    },
    { eli: 0, eurlex: 0, caseLaw: 0, uodo: 0, uzp: 0, sn: 0 }
  )
}

// Normalize a SourceItem's fields to handle camelCase / snake_case / PascalCase variants
function normalizeSource(source: any): SourceItem & { court?: string; signature?: string; date?: string; summary?: string } {
  return {
    type: source.type || source.Type || '',
    title: source.title || source.Title || '',
    url: source.url || source.Url || '',
    act_id: source.actId || source.act_id || source.ActId || '',
    article_num: source.articleNum || source.article_num || source.ArticleNum || '',
    court: source.court || source.Court || '',
    signature: source.signature || source.Signature || '',
    date: source.date || source.Date || '',
    summary: source.summary || source.Summary || '',
  }
}

// Type badge styling
const SOURCE_TYPE_CONFIG: Record<string, { label: string; bg: string; text: string; icon: typeof BookOpen }> = {
  eli: { label: 'ELI', bg: 'bg-blue-100', text: 'text-blue-700', icon: BookOpen },
  eurlex: { label: 'EUR-Lex', bg: 'bg-purple-100', text: 'text-purple-700', icon: Scale },
  case_law: { label: 'Orzecz.', bg: 'bg-amber-100', text: 'text-amber-700', icon: Database },
  uodo: { label: 'UODO', bg: 'bg-green-100', text: 'text-green-700', icon: Shield },
  uzp: { label: 'KIO/UZP', bg: 'bg-teal-100', text: 'text-teal-700', icon: Gavel },
  sn: { label: 'SN', bg: 'bg-red-100', text: 'text-red-700', icon: Scale },
}

// Sources List Component — clickable list with type badges, titles, URLs
function SourcesList({ sources }: { sources: any[] }) {
  if (!sources || sources.length === 0) {
    return <p className="text-sm text-gray-400 py-2">Brak szczegolowych zrodel</p>
  }

  return (
    <div className="divide-y divide-gray-100">
      {sources.map((raw, idx) => {
        const source = normalizeSource(raw)
        const config = SOURCE_TYPE_CONFIG[source.type] || {
          label: source.type,
          bg: 'bg-gray-100',
          text: 'text-gray-700',
          icon: BookOpen,
        }

        return (
          <div key={idx} className="flex items-start gap-3 py-2.5 px-1">
            {/* Type badge */}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${config.bg} ${config.text}`}
            >
              <config.icon className="w-3 h-3" />
              {config.label}
            </span>

            {/* Title + metadata */}
            <div className="flex-1 min-w-0">
              <span className="text-sm text-gray-800">
                {source.title || source.act_id || source.signature || 'Bez tytulu'}
              </span>
              {/* Extra metadata for case law / UODO */}
              {(source.court || source.signature || source.date) && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {[source.court, source.signature, source.date].filter(Boolean).join(' | ')}
                </div>
              )}
              {source.summary && (
                <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                  {source.summary}
                </div>
              )}
            </div>

            {/* Open link */}
            {source.url && (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors whitespace-nowrap"
              >
                Otworz
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Sources Bar Component
function SourcesBar({ metadata }: { metadata?: OpinionMetadata }) {
  const [expanded, setExpanded] = useState(false)

  if (!metadata) return null

  // Handle camelCase (.NET with policy), snake_case (Python), and PascalCase (.NET without policy)
  const md = metadata as any
  const detailedSources = md.detailedSources || md.detailed_sources || md.DetailedSources || []
  const citationsVerified = md.citationsVerified ?? md.citations_verified ?? md.CitationsVerified ?? 0
  const citationsTotal = md.citationsTotal ?? md.citations_total ?? md.CitationsTotal ?? 0

  const counts = detailedSources.length > 0
    ? countSourcesByType(detailedSources)
    : { eli: 0, eurlex: 0, caseLaw: 0, uodo: 0, uzp: 0, sn: 0 }

  const totalSources = counts.eli + counts.eurlex + counts.caseLaw + counts.uodo + counts.uzp + counts.sn
  const verificationText = `${citationsVerified}/${citationsTotal}`

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-4">
      {/* Counts row */}
      <div className="p-4">
        <div className="grid grid-cols-7 gap-4 text-center">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <BookOpen className="w-4 h-4" />
              <span>ELI (prawo PL)</span>
            </div>
            <span className="text-2xl font-bold text-blue-600">{counts.eli}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <Scale className="w-4 h-4" />
              <span>EUR-Lex (prawo UE)</span>
            </div>
            <span className="text-2xl font-bold text-blue-600">{counts.eurlex}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <Database className="w-4 h-4" />
              <span>Orzecznictwo</span>
            </div>
            <span className="text-2xl font-bold text-blue-600">{counts.caseLaw}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <Shield className="w-4 h-4" />
              <span>Decyzje UODO</span>
            </div>
            <span className="text-2xl font-bold text-blue-600">{counts.uodo}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <Gavel className="w-4 h-4" />
              <span>KIO/UZP</span>
            </div>
            <span className="text-2xl font-bold text-blue-600">{counts.uzp}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <Scale className="w-4 h-4" />
              <span>SN</span>
            </div>
            <span className="text-2xl font-bold text-blue-600">{counts.sn}</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 text-gray-500 text-xs mb-1">
              <Check className="w-4 h-4" />
              <span>Weryfikacja</span>
            </div>
            <span className="text-2xl font-bold text-green-600">{verificationText}</span>
          </div>
        </div>
      </div>

      {/* Expand/collapse toggle for detailed sources */}
      {totalSources > 0 && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-center gap-2 py-2 border-t border-gray-100 text-sm text-gray-500 hover:text-blue-600 hover:bg-gray-50 transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Ukryj szczegoly zrodel ({totalSources})
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Pokaz szczegoly zrodel ({totalSources})
              </>
            )}
          </button>

          {/* Detailed sources list */}
          {expanded && (
            <div className="border-t border-gray-100 px-4 pb-3">
              <SourcesList sources={detailedSources} />
            </div>
          )}
        </>
      )}
    </div>
  )
}

// Conversation Actions Component
function ConversationActions({
  status,
  version,
  onAccept,
  onRevise,
  onHistory,
  isLoading,
}: {
  status: OpinionState['status']
  version: number
  onAccept: () => void
  onRevise: () => void
  onHistory: () => void
  isLoading: boolean
}) {
  if (status !== 'awaiting_feedback' && status !== 'completed') return null

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Co chcesz zrobic?</span>
        </div>
        <div className="text-sm text-gray-500">
          Wersja: <span className="font-bold text-blue-600">{version}</span>
        </div>
      </div>
      <div className="flex gap-3 mt-3">
        <button
          onClick={onAccept}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Check className="w-4 h-4" />
          Akceptuj opinie
        </button>
        <button
          onClick={onRevise}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Edit3 className="w-4 h-4" />
          Popraw opinie
        </button>
        <button
          onClick={onHistory}
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <History className="w-4 h-4" />
          Historia wersji
        </button>
      </div>
    </div>
  )
}

// Revision Dialog Component
function RevisionDialog({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: {
  isOpen: boolean
  onClose: () => void
  onSubmit: (feedback: string) => void
  isLoading: boolean
}) {
  const [feedback, setFeedback] = useState('')

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (feedback.trim()) {
      onSubmit(feedback)
      setFeedback('')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4">Popraw opinie</h3>
        <form onSubmit={handleSubmit}>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Opisz co chcesz zmienic w opinii, np. 'Dodaj wiecej informacji o terminach', 'Skroc sekcje o ryzykach'..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={4}
            required
            autoFocus
          />
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isLoading || !feedback.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Edit3 className="w-4 h-4" />
              )}
              Popraw
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// History Dialog Component
function HistoryDialog({
  isOpen,
  onClose,
  versions,
  currentVersion,
  onLoadVersion,
  isLoading,
}: {
  isOpen: boolean
  onClose: () => void
  versions: OpinionVersion[]
  currentVersion: number
  onLoadVersion: (version: OpinionVersion) => void
  isLoading: boolean
}) {
  if (!isOpen) return null

  const formatDate = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return timestamp
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <History className="w-5 h-5" />
            Historia wersji opinii
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Ladowanie historii...</span>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Brak historii wersji</p>
              <p className="text-sm mt-1">Historia pojawi sie po wygenerowaniu pierwszej opinii</p>
            </div>
          ) : (
            <div className="space-y-4">
              {versions.map((v) => (
                <div
                  key={v.version}
                  className={`border rounded-lg p-4 transition-colors ${
                    v.version === currentVersion
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-lg">Wersja {v.version}</span>
                      {v.version === currentVersion && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                          Aktualna
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      {formatDate(v.timestamp)}
                    </div>
                  </div>

                  {v.instructions && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Instrukcje:</span>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {v.instructions.length > 100
                          ? v.instructions.substring(0, 100) + '...'
                          : v.instructions}
                      </p>
                    </div>
                  )}

                  {v.feedback && (
                    <div className="mb-2">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">Feedback:</span>
                      <p className="text-sm text-orange-600 mt-0.5">
                        {v.feedback.length > 100
                          ? v.feedback.substring(0, 100) + '...'
                          : v.feedback}
                      </p>
                    </div>
                  )}

                  <div className="bg-gray-50 rounded p-3 mb-3">
                    <p className="text-sm text-gray-700 line-clamp-3">
                      {v.opinion.substring(0, 250)}
                      {v.opinion.length > 250 && '...'}
                    </p>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Check className="w-3 h-3" />
                      <span>
                        Cytaty: {v.citationsVerified}/{v.citationsTotal}
                      </span>
                    </div>
                    {v.version !== currentVersion && (
                      <button
                        onClick={() => onLoadVersion(v)}
                        className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                      >
                        Zaladuj wersje
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Zamknij
          </button>
        </div>
      </div>
    </div>
  )
}

// Document Picker Component
function DocumentPicker({
  isOpen,
  onClose,
  documents,
  selectedIds,
  onSelect,
  isLoading,
}: {
  isOpen: boolean
  onClose: () => void
  documents: Document[]
  selectedIds: string[]
  onSelect: (ids: string[]) => void
  isLoading: boolean
}) {
  const [localSelected, setLocalSelected] = useState<string[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  // Reset local selection when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedIds)
    }
  }, [isOpen, selectedIds])

  if (!isOpen) return null

  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.type.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const toggleDocument = (docId: string) => {
    setLocalSelected(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    )
  }

  const handleConfirm = () => {
    onSelect(localSelected)
    onClose()
  }

  const handleSelectAll = () => {
    setLocalSelected(filteredDocuments.map(d => d.id))
  }

  const handleClearAll = () => {
    setLocalSelected([])
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pl-PL')
    } catch {
      return dateString
    }
  }

  const getDocTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      legal_opinion: 'Opinia',
      response_to_claim: 'Odpowiedź na pozew',
      claim: 'Pozew',
      appeal: 'Apelacja',
      memo: 'Notatka',
      contract: 'Umowa',
    }
    return labels[type] || type
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Files className="w-5 h-5" />
            Wybierz dokumenty z projektu
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Szukaj dokumentów..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Selection actions */}
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm text-gray-500">
            Wybrano: <span className="font-medium text-blue-600">{localSelected.length}</span> z {documents.length}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Zaznacz wszystkie
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={handleClearAll}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Wyczyść
            </button>
          </div>
        </div>

        {/* Document list */}
        <div className="overflow-y-auto flex-1 border border-gray-200 rounded-lg">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Ładowanie dokumentów...</span>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Files className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Brak dokumentów do wyboru</p>
              <p className="text-sm mt-1">
                {searchTerm ? 'Spróbuj inną frazę wyszukiwania' : 'Dodaj dokumenty do projektu'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredDocuments.map((doc) => (
                <label
                  key={doc.id}
                  className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    localSelected.includes(doc.id) ? 'bg-blue-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={localSelected.includes(doc.id)}
                    onChange={() => toggleDocument(doc.id)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {doc.isGenerated ? (
                        <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      ) : (
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="font-medium text-gray-900 truncate">{doc.title}</span>
                      {doc.isGenerated && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">AI</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="bg-gray-100 px-2 py-0.5 rounded">{getDocTypeLabel(doc.type)}</span>
                      <span>{formatDate(doc.createdAt)}</span>
                      {doc.content && (
                        <span>{doc.content.length.toLocaleString()} znaków</span>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={localSelected.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="w-4 h-4" />
            Wybierz ({localSelected.length})
          </button>
        </div>
      </div>
    </div>
  )
}

// Save Opinion Dialog Component
function SaveOpinionDialog({
  isOpen,
  onClose,
  onSave,
  isLoading,
  error,
  defaultTitle,
}: {
  isOpen: boolean
  onClose: () => void
  onSave: (title: string) => void
  isLoading: boolean
  error: string | null
  defaultTitle: string
}) {
  const [title, setTitle] = useState('')

  // Reset title when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTitle(defaultTitle)
    }
  }, [isOpen, defaultTitle])

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (title.trim()) {
      onSave(title.trim())
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          Zapisz opinie
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tytul dokumentu
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Wprowadz tytul opinii..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              required
              autoFocus
            />
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isLoading || !title.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Zapisz
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Uncompleted Queries Popup Component
function DraftsPopup({
  isOpen,
  onClose,
  sessions,
  onResume,
  onDelete,
}: {
  isOpen: boolean
  onClose: () => void
  sessions: OpinionSessionListItem[]
  onResume: (sessionId: string) => void
  onDelete: (sessionId: string) => void
}) {
  if (!isOpen || sessions.length === 0) return null

  const formatTimeAgo = (dateString: string) => {
    try {
      const diff = Date.now() - new Date(dateString).getTime()
      const minutes = Math.floor(diff / 60000)
      if (minutes < 1) return 'przed chwila'
      if (minutes < 60) return `${minutes} min temu`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours} godz. temu`
      const days = Math.floor(hours / 24)
      return `${days} dni temu`
    } catch {
      return dateString
    }
  }

  const docTypeLabels: Record<string, string> = {
    legal_opinion: 'Opinia',
    response_to_claim: 'Odpowiedź na pozew',
    claim: 'Pozew',
    appeal: 'Apelacja',
    cassation: 'Skarga kasacyjna',
    memo: 'Notatka prawna',
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold">
            Niezakonczone zapytania ({sessions.length})
          </h3>
        </div>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto mb-4">
          {sessions.map((session) => (
            <div
              key={session.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
            >
              <p className="text-sm font-medium text-gray-900 truncate">
                {session.query || session.title || 'Szkic'}
              </p>
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                <span>{docTypeLabels[session.docType] || session.docType}</span>
                <span>·</span>
                <span>{session.currentVersion === 0 ? 'Szkic' : `v${session.currentVersion}`}</span>
                <span>·</span>
                <span>{formatTimeAgo(session.updatedAt)}</span>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={() => onDelete(session.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  Usun
                </button>
                <button
                  onClick={() => onResume(session.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Kontynuuj
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
        >
          Nowa opinia
        </button>
      </div>
    </div>
  )
}

export default function OpinionGeneratorPage() {
  const [query, setQuery] = useState('')
  const [instructions, setInstructions] = useState('')
  const [documentContent, setDocumentContent] = useState('')
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [showFileUpload, setShowFileUpload] = useState(false)
  const [role, setRole] = useState('neutral')
  const [docType, setDocType] = useState('legal_opinion')
  // Source options always enabled (UI hidden per request)
  const useUodo = true
  const useUzp = true
  const useSn = true
  const useNsa = true
  const useMs = true
  const useEurlex = true
  const useCaseLaw = true

  // Document selection from project
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([])
  const [showDocumentPicker, setShowDocumentPicker] = useState(false)

  // Persistent session state
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentDbSessionId, setCurrentDbSessionId] = useState<string | null>(null)
  const [persistedTurns, setPersistedTurns] = useState<OpinionSessionTurnDetail[]>([])
  const sessionResumedRef = useRef(false)

  // Shortcut params handling
  const { shortcutData, clearShortcutParams, hasShortcutParams } = useShortcutParamsWithClear()
  const shortcutAppliedRef = useRef(false)

  // Apply shortcut params to form (only once on mount)
  useEffect(() => {
    if (shortcutData && hasShortcutParams && !shortcutAppliedRef.current) {
      shortcutAppliedRef.current = true
      setQuery(shortcutData.promptTemplate)
      if (shortcutData.instructions) {
        setInstructions(shortcutData.instructions)
      }
      if (shortcutData.docType) {
        setDocType(shortcutData.docType)
      }
      if (shortcutData.role) {
        setRole(shortcutData.role)
      }
      // Clear the URL params after applying
      clearShortcutParams()
    }
  }, [shortcutData, hasShortcutParams, clearShortcutParams])

  const { currentProject } = useProject()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const generationContext = useGeneration()

  // Drafts popup state
  const [showDraftsPopup, setShowDraftsPopup] = useState(false)
  const draftsPopupShownRef = useRef(false)

  // Fetch active sessions for current project (for popup)
  const { data: activeSessions = [], refetch: refetchSessions } = useQuery({
    queryKey: ['opinion-sessions', currentProject?.id],
    queryFn: () => opinionSessionsApi.list(currentProject!.id).then(r => r.data),
    enabled: !!currentProject,
    select: (data) => data.filter(s =>
      s.status === 'Active' && s.id !== currentDbSessionId
    ),
  })

  // Lazy draft session creation — only when form is first changed
  const draftCreatingRef = useRef(false)

  // Refs to track latest form values for use in async callbacks (avoids stale closures)
  const latestFormRef = useRef({ query, instructions, role, docType, selectedDocIds, uploadedFileName })
  latestFormRef.current = { query, instructions, role, docType, selectedDocIds, uploadedFileName }

  const buildSaveData = () => ({
    query: latestFormRef.current.query || undefined,
    instructions: latestFormRef.current.instructions || undefined,
    role: latestFormRef.current.role,
    docType: latestFormRef.current.docType,
    sourceConfig: JSON.stringify({
      useEurlex, useCaseLaw, useUodo, useUzp, useSn, useNsa, useMs,
      selectedDocIds: latestFormRef.current.selectedDocIds,
      uploadedFileName: latestFormRef.current.uploadedFileName,
    }),
    title: latestFormRef.current.query.trim().substring(0, 100) || 'Szkic',
  })

  const ensureDraftSession = useCallback(async (): Promise<string | null> => {
    if (currentDbSessionId) return currentDbSessionId
    if (draftCreatingRef.current || !currentProject) return null

    draftCreatingRef.current = true
    try {
      const res = await opinionSessionsApi.create({
        projectId: currentProject.id,
        query: latestFormRef.current.query || '',
        role: latestFormRef.current.role,
        docType: latestFormRef.current.docType,
      })
      const newId = res.data.id
      setCurrentDbSessionId(newId)
      // Invalidate sessions cache so next mount/popup sees this draft
      queryClient.invalidateQueries({ queryKey: ['opinion-sessions'] })
      // Update with latest form data (may have changed during async create)
      opinionSessionsApi.update(newId, buildSaveData())
        .catch(err => console.error('[OpinionGenerator] Post-create save failed:', err))
      return newId
    } catch (err) {
      console.error('[OpinionGenerator] Failed to create draft session:', err)
      draftCreatingRef.current = false
      return null
    }
  }, [currentDbSessionId, currentProject, queryClient])

  // Auto-save form state to database:
  // - First change: create session immediately (no debounce) so navigating away doesn't lose it
  // - Subsequent changes: debounce updates by 5s
  // - On unmount: flush pending saves immediately
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const pendingSaveRef = useRef<{ sessionId: string } | null>(null)
  const initialRenderRef = useRef(true)
  useEffect(() => {
    // Skip the initial render to avoid creating a session before the user types
    if (initialRenderRef.current) {
      initialRenderRef.current = false
      return
    }
    // Don't auto-save if form is completely empty
    if (!query && !instructions && selectedDocIds.length === 0) return

    if (!currentDbSessionId) {
      // No session yet — create immediately (no debounce) so it survives navigation
      // ensureDraftSession uses latestFormRef internally, so it always saves latest data
      ensureDraftSession()
    } else {
      // Session exists — debounce updates
      clearTimeout(saveTimeoutRef.current)
      pendingSaveRef.current = { sessionId: currentDbSessionId }
      saveTimeoutRef.current = setTimeout(() => {
        opinionSessionsApi.update(currentDbSessionId, buildSaveData())
          .catch(err => console.error('[OpinionGenerator] Auto-save failed:', err))
        pendingSaveRef.current = null
      }, 5000)
    }

    return () => {
      clearTimeout(saveTimeoutRef.current)
      // Flush pending save on unmount so data isn't lost when navigating away
      if (pendingSaveRef.current) {
        opinionSessionsApi.update(pendingSaveRef.current.sessionId, buildSaveData())
          .catch(err => console.error('[OpinionGenerator] Unmount save failed:', err))
        pendingSaveRef.current = null
      }
    }
  }, [query, instructions, role, docType, selectedDocIds, currentDbSessionId, uploadedFileName])

  // Resume session from URL param
  useEffect(() => {
    const sessionId = searchParams.get('sessionId')
    if (sessionId && !sessionResumedRef.current) {
      sessionResumedRef.current = true
      opinionSessionsApi.get(sessionId).then(res => {
        const session = res.data
        setCurrentDbSessionId(session.id)
        setQuery(session.query)
        if (session.instructions) setInstructions(session.instructions)
        setRole(session.role)
        setDocType(session.docType)

        // Restore source config (selectedDocIds, uploadedFileName)
        if (session.sourceConfig) {
          try {
            const config = JSON.parse(session.sourceConfig)
            if (config.selectedDocIds) setSelectedDocIds(config.selectedDocIds)
            if (config.uploadedFileName) setUploadedFileName(config.uploadedFileName)
          } catch { /* ignore */ }
        }

        // Restore turns
        if (session.turns && session.turns.length > 0) {
          setPersistedTurns(session.turns)
          const latestTurn = session.turns[session.turns.length - 1]
          setOpinion({
            text: latestTurn.opinionText,
            version: latestTurn.version,
            status: 'awaiting_feedback',
            sessionId: session.externalSessionId || undefined,
            metadata: latestTurn.sourcesUsed ? {
              detailedSources: (() => {
                try { return JSON.parse(latestTurn.sourcesUsed!) } catch { return [] }
              })(),
              citationsVerified: latestTurn.citationsVerified,
              citationsTotal: latestTurn.citationsTotal,
            } : undefined,
          })
        }

        // Clear the URL param to avoid re-loading on refresh
        setSearchParams({}, { replace: true })
      }).catch(err => {
        console.error('[OpinionGenerator] Failed to resume session:', err)
        setSearchParams({}, { replace: true })
      })
    }
  }, [searchParams, setSearchParams])

  // Fetch documents for current project
  const { data: projectDocumentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['documents', { projectId: currentProject?.id }],
    queryFn: () => documentsApi.list({ projectId: currentProject?.id }).then(r => r.data),
    enabled: !!currentProject,
  })

  const projectDocuments = projectDocumentsData?.items || []

  // Mutation for auto-saving uploaded documents to project
  const createDocumentMutation = useMutation({
    mutationFn: (data: { title: string; type: string; content: string; projectId: string }) =>
      documentsApi.create(data).then(r => r.data),
    onSuccess: (newDoc) => {
      // Refresh documents list
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      // Auto-select the new document
      setSelectedDocIds(prev => [...prev, newDoc.id])
    },
    onError: (error: any) => {
      console.error('[Auto-save] Failed to save document:', error)
      // Show error details for debugging
      const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error'
      console.error('[Auto-save] Error details:', errorMessage)
      alert(`Nie udało się zapisać dokumentu do projektu: ${errorMessage}`)
    },
  })

  // Get selected documents info for display
  const selectedDocuments = projectDocuments.filter(doc => selectedDocIds.includes(doc.id))

  // Document type mapping from frontend docType to backend DocumentType enum values
  const docTypeToEnumMapping: Record<string, number> = {
    'legal_opinion': 0,      // OpiniaPrawna
    'response_to_claim': 9,  // OdpowiedzNaPozew
    'claim': 1,              // Pozew
    'appeal': 5,             // Odwolanie
    'cassation': 6,          // Skarga
    'memo': 8,               // Notatka
  }

  // Mutation for saving accepted opinion as a document
  const saveOpinionMutation = useMutation({
    mutationFn: (data: { title: string; content: string; type: number; projectId: string; sourcesUsed?: string; metadata?: object }) =>
      documentsApi.create(data as any).then(r => r.data),
    onSuccess: (savedDoc) => {
      // Refresh documents list
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      // Close dialog and navigate to saved document
      setShowSaveDialog(false)
      setSaveError(null)

      // Mark session as completed and link to document
      if (currentDbSessionId) {
        opinionSessionsApi.update(currentDbSessionId, {
          status: 'Completed',
          resultDocumentId: savedDoc.id,
        }).catch(err => console.error('[OpinionGenerator] Failed to mark session completed:', err))
      }

      navigate(`/documents/${savedDoc.id}`)
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.message || 'Nieznany blad'
      setSaveError(errorMessage)
      console.error('[saveOpinionMutation] Failed to save opinion:', error)
    },
  })

  // Check if any selected documents might have empty content (based on list data - content not included in list)
  // We'll fetch and verify when submitting

  // Opinion state
  const [opinion, setOpinion] = useState<OpinionState>({
    text: '',
    version: 0,
    status: 'idle',
  })

  // Dialogs
  const [showRevisionDialog, setShowRevisionDialog] = useState(false)
  const [showHistoryDialog, setShowHistoryDialog] = useState(false)
  const [showAnonymization, setShowAnonymization] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Version history
  const [versionHistory, setVersionHistory] = useState<OpinionVersion[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)

  // Show drafts popup on mount if there are active sessions (needs opinion state)
  // Note: activeSessions already excludes currentDbSessionId via the select filter,
  // so we don't need to check !currentDbSessionId here
  useEffect(() => {
    if (
      activeSessions.length > 0 &&
      !searchParams.get('sessionId') &&
      !hasShortcutParams &&
      opinion.status === 'idle' &&
      !draftsPopupShownRef.current
    ) {
      draftsPopupShownRef.current = true
      setShowDraftsPopup(true)
    }
  }, [activeSessions.length, opinion.status])

  // Detect completed background generation (needs opinion state setter)
  useEffect(() => {
    if (
      generationContext.activeGeneration?.status === 'completed' &&
      generationContext.activeGeneration.sessionId === currentDbSessionId
    ) {
      // Reload session to get the saved turn
      opinionSessionsApi.get(currentDbSessionId!).then(res => {
        const session = res.data
        if (session.turns.length > 0) {
          const latestTurn = session.turns[session.turns.length - 1]
          setOpinion({
            text: latestTurn.opinionText,
            version: latestTurn.version,
            status: 'awaiting_feedback',
            sessionId: session.externalSessionId || undefined,
            metadata: latestTurn.sourcesUsed ? {
              detailedSources: (() => {
                try { return JSON.parse(latestTurn.sourcesUsed!) } catch { return [] }
              })(),
              citationsVerified: latestTurn.citationsVerified,
              citationsTotal: latestTurn.citationsTotal,
            } : undefined,
          })
          setPersistedTurns(session.turns)
        }
      }).catch(err => console.error('[OpinionGenerator] Failed to reload session:', err))
      generationContext.clearGeneration()
    }
  }, [generationContext.activeGeneration?.status, currentDbSessionId])

  const handleUploadComplete = (results: FileUploadResult[]) => {
    const successfulResults = results.filter(r => r.status === 'success' && r.text)
    if (successfulResults.length > 0) {
      const combinedText = successfulResults.map(r => r.text).join('\n\n---\n\n')
      setDocumentContent(combinedText)
      setUploadedFileName(successfulResults.map(r => r.filename).join(', '))
      setShowFileUpload(false)

      // Auto-save uploaded document to project
      if (currentProject) {
        const title = successfulResults.length === 1
          ? successfulResults[0].filename.replace(/\.[^/.]+$/, '')  // Remove file extension
          : `Upload ${new Date().toLocaleDateString('pl-PL')}`

        createDocumentMutation.mutate({
          title,
          type: 'Notatka',  // Use 'Notatka' (memo) for uploaded documents
          content: combinedText,
          projectId: currentProject.id,
        })
      }
    }
  }

  const clearDocument = () => {
    setDocumentContent('')
    setUploadedFileName(null)
  }

  const removeSelectedDocument = (docId: string) => {
    setSelectedDocIds(prev => prev.filter(id => id !== docId))
  }

  const clearAllSelectedDocuments = () => {
    setSelectedDocIds([])
  }

  // Generate opinion mutation
  const generateMutation = useMutation({
    mutationFn: async (data: GenerateOpinionRequest) => {
      const res = await api.post('/generation/opinion', data)
      return res.data
    },
    onMutate: () => {
      setOpinion(prev => ({ ...prev, status: 'generating', text: '' }))
    },
    onSuccess: (data) => {
      // Handle response - extract text and metadata
      let fullText = ''
      let metadata: OpinionMetadata | undefined
      let sessionId: string | undefined

      if (Array.isArray(data)) {
        // Streaming response - array of chunks
        fullText = data.map((chunk: { text?: string }) => chunk.text || '').join('')

        // Find metadata in the last chunk (check multiple field name variants)
        const lastChunk = data.find((chunk: any) => {
          const isFinal = chunk.isFinal || chunk.IsFinal || chunk.is_final
          const hasMetadata = chunk.metadata || chunk.Metadata
          return isFinal && hasMetadata
        })

        if (lastChunk) {
          metadata = lastChunk.metadata || lastChunk.Metadata
          sessionId = metadata?.sessionId || metadata?.session_id || (metadata as any)?.SessionId
        }
      } else if (data.chunks) {
        fullText = data.full_text || data.chunks.map((c: { text?: string }) => c.text || '').join('')
        metadata = data.metadata
        sessionId = data.metadata?.sessionId || data.session_id
      } else {
        fullText = data.content || data.text || data.opinion || ''
        metadata = data.metadata
        sessionId = data.metadata?.sessionId || data.session_id
      }

      setOpinion({
        text: fullText,
        metadata,
        sessionId: sessionId,
        version: 1,
        status: 'awaiting_feedback',
      })

      // Auto-save to persistent session
      if (currentProject) {
        const md = metadata as any
        const detailedSources = md?.detailedSources || md?.detailed_sources || md?.DetailedSources || []
        const citVerified = md?.citationsVerified ?? md?.citations_verified ?? 0
        const citTotal = md?.citationsTotal ?? md?.citations_total ?? 0
        const sourcesJson = detailedSources.length > 0 ? JSON.stringify(detailedSources) : undefined

        if (!currentDbSessionId) {
          // Create session first, then add turn
          opinionSessionsApi.create({
            projectId: currentProject.id,
            query,
            instructions: instructions || undefined,
            role,
            docType,
            sourceConfig: JSON.stringify({ useEurlex, useCaseLaw, useUodo, useUzp, useSn }),
          }).then(res => {
            const newSessionId = res.data.id
            setCurrentDbSessionId(newSessionId)
            return opinionSessionsApi.addTurn(newSessionId, {
              opinionText: fullText,
              type: 'Generation',
              externalSessionId: sessionId,
              sourcesUsed: sourcesJson,
              citationsVerified: citVerified,
              citationsTotal: citTotal,
              metadata: metadata ? JSON.stringify({ generationTimeMs: md?.generationTimeMs || md?.generation_time_ms }) : undefined,
            })
          }).then(turnRes => {
            setPersistedTurns(prev => [...prev, turnRes.data])
          }).catch(err => console.error('[OpinionGenerator] Failed to create session:', err))
        } else {
          // Session exists, just add turn
          opinionSessionsApi.addTurn(currentDbSessionId, {
            opinionText: fullText,
            type: 'Generation',
            externalSessionId: sessionId,
            sourcesUsed: sourcesJson,
            citationsVerified: citVerified,
            citationsTotal: citTotal,
          }).then(turnRes => {
            setPersistedTurns(prev => [...prev, turnRes.data])
          }).catch(err => console.error('[OpinionGenerator] Failed to save turn:', err))
        }
      }
    },
    onError: () => {
      setOpinion(prev => ({ ...prev, status: 'error' }))
    },
  })

  // Revision mutation - uses same endpoint with feedback parameters
  const reviseMutation = useMutation({
    mutationFn: async (feedback: string) => {
      // Use the same /generation/opinion endpoint with revision parameters
      const res = await api.post('/generation/opinion', {
        query,
        instructions: instructions || undefined,
        documentContent: documentContent || undefined,
        useAnonymization: true,
        role,
        docType,
        useUodo,
        useUzp,
        useSn,
        useNsa,
        useMs,
        useEurlex,
        useCaseLaw,
        // Revision-specific parameters
        sessionId: opinion.sessionId,
        previousOpinion: opinion.text,
        feedback: feedback,
      })
      return res.data
    },
    onMutate: () => {
      setOpinion(prev => ({ ...prev, status: 'generating' }))
      setShowRevisionDialog(false)
    },
    onSuccess: (data) => {
      // Handle response - same as generateMutation
      let fullText = ''
      let metadata: OpinionMetadata | undefined
      let sessionId: string | undefined

      if (Array.isArray(data)) {
        fullText = data.map((chunk: { text?: string }) => chunk.text || '').join('')
        const lastChunk = data.find((chunk: { is_final?: boolean; metadata?: OpinionMetadata; session_id?: string }) =>
          chunk.is_final && chunk.metadata)
        if (lastChunk) {
          metadata = lastChunk.metadata
          sessionId = lastChunk.session_id
        }
      } else if (data.chunks) {
        fullText = data.full_text || data.chunks.map((c: { text?: string }) => c.text || '').join('')
        metadata = data.metadata
        sessionId = data.session_id
      } else {
        fullText = data.content || data.text || data.opinion || ''
        metadata = data.metadata
        sessionId = data.session_id
      }

      const newVersion = opinion.version + 1

      setOpinion(prev => ({
        ...prev,
        text: fullText || prev.text,
        version: newVersion,
        status: 'awaiting_feedback',
        metadata: metadata || prev.metadata,
        sessionId: sessionId || prev.sessionId,
      }))

      // Auto-save revision turn
      if (currentDbSessionId) {
        const md = metadata as any
        const detailedSources = md?.detailedSources || md?.detailed_sources || md?.DetailedSources || []
        const citVerified = md?.citationsVerified ?? md?.citations_verified ?? 0
        const citTotal = md?.citationsTotal ?? md?.citations_total ?? 0
        const sourcesJson = detailedSources.length > 0 ? JSON.stringify(detailedSources) : undefined

        opinionSessionsApi.addTurn(currentDbSessionId, {
          opinionText: fullText || opinion.text,
          type: 'Revision',
          userFeedback: reviseMutation.variables || undefined,
          externalSessionId: sessionId || opinion.sessionId,
          sourcesUsed: sourcesJson,
          citationsVerified: citVerified,
          citationsTotal: citTotal,
        }).then(turnRes => {
          setPersistedTurns(prev => [...prev, turnRes.data])
        }).catch(err => console.error('[OpinionGenerator] Failed to save revision turn:', err))
      }
    },
    onError: () => {
      setOpinion(prev => ({ ...prev, status: 'error' }))
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return

    let combinedContent = documentContent  // Uploaded file content

    // Fetch content from selected project documents
    if (selectedDocIds.length > 0) {
      try {
        const docResponses = await Promise.all(
          selectedDocIds.map(id => documentsApi.get(id).then(r => r.data))
        )

        // Check for empty documents
        const emptyDocs = docResponses.filter(doc => !doc.content || doc.content.trim().length === 0)
        if (emptyDocs.length > 0) {
          const emptyTitles = emptyDocs.map(d => d.title).join(', ')
          console.warn(`[handleSubmit] Documents with empty content: ${emptyTitles}`)
          alert(`Uwaga: Następujące dokumenty nie mają treści i zostaną pominięte: ${emptyTitles}`)
        }

        const docContents = docResponses.map(doc => doc.content).filter(c => c && c.trim().length > 0)

        const existingDocsText = docContents.join('\n\n---\n\n')
        combinedContent = combinedContent
          ? `${existingDocsText}\n\n---\n\n${combinedContent}`
          : existingDocsText

      } catch (error) {
        console.error('[handleSubmit] Failed to fetch selected documents:', error)
        alert('Błąd podczas pobierania treści dokumentów. Sprawdź konsolę.')
      }
    }

    if (!combinedContent && selectedDocIds.length > 0) {
      console.warn('[handleSubmit] No content to send despite selected documents!')
    }

    // Ensure a draft session exists before starting generation
    const sessionId = currentDbSessionId || await ensureDraftSession()

    const generationParams = {
      query,
      instructions: instructions || undefined,
      documentContent: combinedContent || undefined,
      useAnonymization: true,
      role,
      docType,
      useUodo,
      useUzp,
      useSn,
      useNsa,
      useMs,
      useEurlex,
      useCaseLaw,
      projectId: currentProject?.id,
    }

    if (sessionId) {
      // Use GenerationContext for background-safe generation
      generationContext.startGeneration(sessionId, generationParams)
      setOpinion(prev => ({ ...prev, status: 'generating', text: '' }))
    } else {
      // Fallback: direct mutation if session creation failed
      generateMutation.mutate(generationParams)
    }
  }

  const handleAccept = () => {
    if (!currentProject) {
      alert('Brak wybranego projektu')
      return
    }
    setShowSaveDialog(true)
    setSaveError(null)
  }

  const handleSaveConfirm = (title: string) => {
    if (!currentProject) return

    // Get opinion content (without anonymization section)
    const ANON_MARKER = '## 🔒 Anonimizacja danych osobowych'
    const anonIndex = opinion.text.indexOf(ANON_MARKER)
    const opinionContent = anonIndex !== -1
      ? opinion.text.substring(0, anonIndex).replace(/\n*---\n*$/, '')
      : opinion.text

    // Extract detailed sources from metadata
    const md = opinion.metadata as any
    const detailedSources = md?.detailedSources || md?.detailed_sources || md?.DetailedSources || []

    // Prepare metadata
    const saveMetadata = {
      sessionId: opinion.sessionId,
      version: opinion.version,
      query: query,
      instructions: instructions || undefined,
      role: role,
      citationsVerified: md?.citationsVerified ?? md?.citations_verified ?? 0,
      citationsTotal: md?.citationsTotal ?? md?.citations_total ?? 0,
    }

    // Get document type enum value
    const docTypeEnumValue = docTypeToEnumMapping[docType] ?? 0

    saveOpinionMutation.mutate({
      title,
      content: opinionContent,
      type: docTypeEnumValue,
      projectId: currentProject.id,
      sourcesUsed: detailedSources.length > 0 ? JSON.stringify(detailedSources) : undefined,
      metadata: saveMetadata,
    })
  }

  const handleRevise = () => {
    setShowRevisionDialog(true)
  }

  const handleRevisionSubmit = (feedback: string) => {
    reviseMutation.mutate(feedback)
  }

  const handleHistory = async () => {
    if (!opinion.sessionId) {
      alert('Historia dostepna tylko po wygenerowaniu opinii')
      return
    }

    setHistoryLoading(true)
    setShowHistoryDialog(true)

    try {
      const res = await opinionApi.getHistory(opinion.sessionId)
      setVersionHistory(res.data.versions || [])
    } catch (e) {
      console.error('[handleHistory] Failed to fetch history:', e)
      setVersionHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }

  const handleLoadVersion = (version: OpinionVersion) => {
    setOpinion(prev => ({
      ...prev,
      text: version.opinion,
      version: version.version,
      metadata: {
        ...prev.metadata,
        citationsVerified: version.citationsVerified,
        citationsTotal: version.citationsTotal,
      },
    }))
    setShowHistoryDialog(false)
  }

  const handleNewOpinion = () => {
    // Archive old draft if it was never used (no turns)
    if (currentDbSessionId && persistedTurns.length === 0) {
      opinionSessionsApi.update(currentDbSessionId, { status: 'Archived' })
        .catch(err => console.error('[OpinionGenerator] Failed to archive draft:', err))
    }

    setQuery('')
    setInstructions('')
    setDocumentContent('')
    setUploadedFileName(null)
    setShowFileUpload(false)
    setSelectedDocIds([])
    setOpinion({ text: '', version: 0, status: 'idle' })
    setVersionHistory([])
    setShowRevisionDialog(false)
    setShowHistoryDialog(false)
    setShowAnonymization(false)
    // Reset persistent session state
    setCurrentDbSessionId(null)
    setPersistedTurns([])
    sessionResumedRef.current = false
    draftCreatingRef.current = false
    initialRenderRef.current = true
  }

  const isLoading = generateMutation.isPending || reviseMutation.isPending ||
    (generationContext.activeGeneration?.status === 'generating' && generationContext.activeGeneration?.sessionId === currentDbSessionId)

  // Drafts popup handlers
  const handleDraftResume = (sessionId: string) => {
    setShowDraftsPopup(false)
    navigate(`/generate?sessionId=${sessionId}`)
  }

  const handleDraftDelete = (sessionId: string) => {
    opinionSessionsApi.delete(sessionId)
      .then(() => refetchSessions())
      .catch(err => console.error('[OpinionGenerator] Failed to delete session:', err))
  }

  // Show message when no project is selected
  if (!currentProject) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="card p-8 text-center">
          <FolderOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            Wybierz projekt
          </h2>
          <p className="text-gray-500 mb-4">
            Aby generowac opinie, wybierz projekt z menu po lewej stronie.
          </p>
          <Link to="/projects" className="btn-primary inline-flex items-center">
            <FolderOpen className="w-4 h-4 mr-2" />
            Przejdz do projektow
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-2">
          <FolderOpen className="w-4 h-4" />
          <span>{currentProject.name}</span>
        </div>
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <Sparkles className="w-8 h-8 text-blue-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Generuj opinię</h1>
        <p className="text-gray-500 mt-2">
          Zadaj pytanie, a otrzymasz analizę z cytatami z aktualnego stanu prawnego
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pytanie prawne
          </label>
          <textarea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="np. Jakie sa terminy wypowiedzenia umowy agencyjnej?"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={4}
            maxLength={10000}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dodatkowe instrukcje (opcjonalnie)
          </label>
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="np. Skup sie na orzecznictwie SN. Uwzglednij prawo UE."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={2}
          />
        </div>

        {/* Role and Document Type dropdowns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rola / Perspektywa:
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Typ dokumentu:
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              {DOC_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Source options: always enabled (useEurlex, useCaseLaw, useUodo default to true) */}

        {/* Document attachment section */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Załącz dokument do analizy (opcjonalnie)
          </label>

          {/* File upload */}
          {uploadedFileName ? (
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                <span className="text-sm text-green-800">{uploadedFileName}</span>
                <span className="text-xs text-green-600">({documentContent.length} znaków)</span>
              </div>
              <button
                type="button"
                onClick={clearDocument}
                className="text-green-600 hover:text-red-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : showFileUpload ? (
            <div className="border border-gray-200 rounded-lg p-4">
              <FileUpload onUploadComplete={handleUploadComplete} />
              <button
                type="button"
                onClick={() => setShowFileUpload(false)}
                className="mt-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Anuluj
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowFileUpload(true)}
              className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              <FileText className="w-5 h-5 mx-auto mb-1" />
              <span className="text-sm">Kliknij, aby załączyć dokument (PDF, DOCX, TXT, obrazy)</span>
            </button>
          )}

          {/* Separator */}
          {projectDocuments.length > 0 && (
            <>
              <div className="flex items-center gap-3 text-gray-400 text-sm">
                <div className="flex-1 border-t border-gray-200"></div>
                <span>lub</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>

              {/* Project documents selection */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowDocumentPicker(true)}
                  className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
                >
                  <Files className="w-5 h-5" />
                  <span className="text-sm">Wybierz dokumenty z projektu ({projectDocuments.length} dostępnych)</span>
                </button>

                {/* Selected documents display */}
                {selectedDocuments.length > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-800">
                        Wybrane dokumenty ({selectedDocuments.length})
                      </span>
                      <button
                        type="button"
                        onClick={clearAllSelectedDocuments}
                        className="text-xs text-blue-600 hover:text-red-500"
                      >
                        Usuń wszystkie
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedDocuments.map(doc => (
                        <span
                          key={doc.id}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-blue-200 rounded-full text-sm text-blue-800"
                        >
                          <FileText className="w-3 h-3" />
                          <span className="max-w-[150px] truncate">{doc.title}</span>
                          <button
                            type="button"
                            onClick={() => removeSelectedDocument(doc.id)}
                            className="text-blue-400 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3">
          {opinion.text && (
            <button
              type="button"
              onClick={handleNewOpinion}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nowa opinia
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || !query.trim()}
            className="btn-primary"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generowanie...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Generuj opinię
              </>
            )}
          </button>
        </div>
      </form>

      {/* Loading popup */}
      {isLoading && !opinion.text && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-full max-w-md mx-4 flex flex-col items-center text-center space-y-4">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            <div>
              <p className="text-lg font-semibold text-gray-800">Trwa generowanie...</p>
              <p className="text-sm text-gray-500 mt-1">
                Dla obszerniejszych materiałów przygotowanie wyniku zwykle zajmuje od 8 do 15 minut — to normalne, analiza nadal trwa.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                generateMutation.reset()
                setOpinion(prev => ({ ...prev, status: 'idle' }))
              }}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 mt-2"
            >
              <X className="w-4 h-4" />
              Anuluj
            </button>
          </div>
        </div>
      )}

      {/* Response */}
      {opinion.text && (
        <div className="space-y-4">
          {/* Sources Bar */}
          <SourcesBar metadata={opinion.metadata} />

          {/* Session Timeline (only show if there are persisted turns with more than 1 version) */}
          {persistedTurns.length > 1 && (
            <SessionTimeline
              turns={persistedTurns}
              currentVersion={opinion.version}
              query={query}
            />
          )}

          {/* Conversation Actions */}
          <ConversationActions
            status={opinion.status}
            version={opinion.version}
            onAccept={handleAccept}
            onRevise={handleRevise}
            onHistory={handleHistory}
            isLoading={isLoading}
          />

          {/* Opinion Content */}
          {(() => {
            const ANON_MARKER = '## 🔒 Anonimizacja danych osobowych'
            const anonIndex = opinion.text.indexOf(ANON_MARKER)
            const opinionContent = anonIndex !== -1 ? opinion.text.substring(0, anonIndex).replace(/\n*---\n*$/, '') : opinion.text
            const anonymizationContent = anonIndex !== -1 ? opinion.text.substring(anonIndex) : null

            return (
              <>
                <div className="card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Wygenerowana opinia</h2>
                    {anonymizationContent && (
                      <button
                        onClick={() => setShowAnonymization(true)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                      >
                        <Shield className="w-4 h-4" />
                        Anonimizacja danych
                      </button>
                    )}
                  </div>
                  <div className="prose max-w-none">
                    <ReactMarkdown>{opinionContent}</ReactMarkdown>
                  </div>
                </div>

                {/* Anonymization Modal */}
                {showAnonymization && anonymizationContent && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div
                      className="fixed inset-0"
                      onClick={() => setShowAnonymization(false)}
                    />
                    <div className="relative bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <Shield className="w-5 h-5 text-amber-600" />
                          Anonimizacja danych osobowych
                        </h3>
                        <button
                          onClick={() => setShowAnonymization(false)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="overflow-y-auto flex-1 prose max-w-none">
                        <ReactMarkdown>{anonymizationContent}</ReactMarkdown>
                      </div>
                      <div className="mt-4 pt-4 border-t">
                        <button
                          onClick={() => setShowAnonymization(false)}
                          className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Zamknij
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )
          })()}
        </div>
      )}

      {/* Error */}
      {(generateMutation.isError || reviseMutation.isError || opinion.status === 'error') && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            Wystapil blad podczas generowania opinii. Sprobuj ponownie.
          </p>
        </div>
      )}

      {/* Revision Dialog */}
      <RevisionDialog
        isOpen={showRevisionDialog}
        onClose={() => setShowRevisionDialog(false)}
        onSubmit={handleRevisionSubmit}
        isLoading={reviseMutation.isPending}
      />

      {/* History Dialog */}
      <HistoryDialog
        isOpen={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        versions={versionHistory}
        currentVersion={opinion.version}
        onLoadVersion={handleLoadVersion}
        isLoading={historyLoading}
      />

      {/* Document Picker Dialog */}
      <DocumentPicker
        isOpen={showDocumentPicker}
        onClose={() => setShowDocumentPicker(false)}
        documents={projectDocuments}
        selectedIds={selectedDocIds}
        onSelect={setSelectedDocIds}
        isLoading={documentsLoading}
      />

      {/* Save Opinion Dialog */}
      <SaveOpinionDialog
        isOpen={showSaveDialog}
        onClose={() => {
          setShowSaveDialog(false)
          setSaveError(null)
        }}
        onSave={handleSaveConfirm}
        isLoading={saveOpinionMutation.isPending}
        error={saveError}
        defaultTitle={query ? `Opinia: ${query.substring(0, 50)}${query.length > 50 ? '...' : ''}` : 'Opinia'}
      />

      {/* Uncompleted Queries Popup */}
      <DraftsPopup
        isOpen={showDraftsPopup}
        onClose={() => setShowDraftsPopup(false)}
        sessions={activeSessions}
        onResume={handleDraftResume}
        onDelete={handleDraftDelete}
      />
    </div>
  )
}
